/**
 * Free Generation Counter
 *
 * Renders a small badge in the header showing how many free customized resumes
 * the user has remaining. Hidden for active subscribers.
 */
import { useState, useRef, useEffect } from 'react';
import { Sparkles, Zap } from 'lucide-react';
import type { UserPreferences } from '../types';

interface FreeGenCounterProps {
  userPreferences?: UserPreferences;
  onUpgrade: () => void;
}

export function FreeGenCounter({ userPreferences, onUpgrade }: FreeGenCounterProps) {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        setTooltipOpen(false);
      }
    }
    if (tooltipOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [tooltipOpen]);

  if (!userPreferences) return null;
  const status = userPreferences.status;
  if (status !== 'trial' && status !== 'hibernating') return null;

  const remaining = userPreferences.free_gen_remaining;
  if (remaining === undefined) return null;

  const isOut = remaining <= 0;
  // Copy must match the server: free generations top back up to 5 on a ROLLING
  // 30-day cycle (shared/generation_checker.py FREE_GEN_REFILL_TARGET=5,
  // FREE_GEN_REFILL_DAYS=30) — there is no calendar-month refill.
  const tooltipText = isOut
    ? "You've used all your free generations. Your balance tops back up to 5 every 30 days, or upgrade to Active for unlimited."
    : `${remaining} free customized-resume generation${remaining === 1 ? '' : 's'} remaining. Tops back up to 5 every 30 days.`;

  return (
    <div className="relative flex items-center gap-1.5" ref={tooltipRef}>
      <button
        type="button"
        onClick={() => setTooltipOpen((v) => !v)}
        onMouseEnter={() => setTooltipOpen(true)}
        onMouseLeave={() => setTooltipOpen(false)}
        data-help-target="mainapp.freegenerations"
        className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
          isOut
            ? 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100'
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
        }`}
        aria-label={tooltipText}
      >
        <Zap className={`w-3.5 h-3.5 ${isOut ? 'text-red-500' : 'text-gray-500'}`} />
        <span className="tabular-nums font-semibold">{remaining}</span>
        <span className="hidden sm:inline">Free Generations</span>
      </button>

      {status === 'trial' && (
        <button
          type="button"
          onClick={onUpgrade}
          data-help-target="mainapp.upgradenow"
          className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-400 hover:to-orange-400 transition-all"
        >
          <Sparkles className="w-3 h-3" />
          <span>Upgrade Now</span>
        </button>
      )}

      {tooltipOpen && (
        <div
          role="tooltip"
          className="absolute top-full right-0 mt-1 w-64 p-2 bg-gray-900 text-white text-xs rounded-md shadow-lg z-50"
        >
          {tooltipText}
        </div>
      )}
    </div>
  );
}
